/**
 * Support ticket client diagnostics
 * Persists breadcrumbs across full tab/browser closes so reports keep prior context.
 */

import { getImpersonationState } from '@/shared/lib/impersonation';

const STORAGE_KEY = 'teamin_support_diagnostics_v1';
const MAX_NAV = 20;
const MAX_HTTP = 40;
const MAX_ERRORS = 15;
const MAX_LIFECYCLE = 30;
const MAX_STRING = 500;
const MAX_STACK = 1500;
const MAX_AGE_MS = 48 * 60 * 60 * 1000;
const PERSIST_DEBOUNCE_MS = 350;

const SENSITIVE_QUERY_KEYS = [
  'token',
  'access_token',
  'refresh_token',
  'password',
  'secret',
  'authorization',
  'api_key',
  'apikey',
  'key',
  'session',
];

const navigationBreadcrumbs = [];
const httpBreadcrumbs = [];
const errorBreadcrumbs = [];
const lifecycleBreadcrumbs = [];

let diagnosticsInitialized = false;
let lastPathname = null;
let persistTimer = null;
let currentSessionId = '';
let continuity = null;
let pageState = 'active';

function nowIso() {
  return new Date().toISOString();
}

function newSessionId() {
  return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function truncate(value, max = MAX_STRING) {
  if (value == null) return value;
  const str = String(value);
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

function pushRing(list, item, max) {
  list.push(item);
  if (list.length > max) {
    list.splice(0, list.length - max);
  }
}

function isFresh(at) {
  if (!at) return false;
  const ts = Date.parse(at);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts <= MAX_AGE_MS;
}

function trimByAge(list, max) {
  return (Array.isArray(list) ? list : []).filter((item) => isFresh(item?.at)).slice(-max);
}

function sanitizePath(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://local';
    const parsed = url.startsWith('http') ? new URL(url) : new URL(url, base);
    const params = new URLSearchParams(parsed.search);
    SENSITIVE_QUERY_KEYS.forEach((key) => {
      if (params.has(key)) params.set(key, '[redacted]');
    });
    const qs = params.toString();
    return truncate(`${parsed.pathname}${qs ? `?${qs}` : ''}${parsed.hash || ''}`, 300);
  } catch {
    return truncate(url.split('?')[0], 300);
  }
}

function getAppVersion() {
  return (
    process.env.REACT_APP_APP_VERSION ||
    process.env.REACT_APP_VERSION ||
    process.env.REACT_APP_BUILD_ID ||
    'web'
  );
}

function schedulePersist() {
  if (typeof window === 'undefined') return;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    persistNow();
  }, PERSIST_DEBOUNCE_MS);
}

function persistNow() {
  if (typeof window === 'undefined') return;
  try {
    const lastState =
      pageState === 'hidden' || pageState === 'background' ? 'background' : 'active';
    const payload = {
      version: 1,
      savedAt: nowIso(),
      lastPathname,
      navigation: navigationBreadcrumbs.slice(-MAX_NAV),
      http: httpBreadcrumbs.slice(-MAX_HTTP),
      errors: errorBreadcrumbs.slice(-MAX_ERRORS),
      lifecycle: lifecycleBreadcrumbs.slice(-MAX_LIFECYCLE),
      sessionMeta: {
        sessionId: currentSessionId,
        lastState,
        lastActiveAt: nowIso(),
        lastBackgroundAt: lastState === 'background' ? nowIso() : null,
      },
      continuity,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

function computeContinuity(prevMeta, nextSessionId) {
  const previousLastActiveAt = prevMeta?.lastActiveAt;
  const gapMs =
    previousLastActiveAt && Number.isFinite(Date.parse(previousLastActiveAt))
      ? Math.max(0, Date.now() - Date.parse(previousLastActiveAt))
      : null;
  const hadPrevious = Boolean(prevMeta?.sessionId);
  const previousLastState = prevMeta?.lastState;
  const likelyForceQuitWhileActive = Boolean(hadPrevious && previousLastState === 'active');
  const closedFromBackground = Boolean(hadPrevious && previousLastState === 'background');
  const restartedAfterFullClose = Boolean(hadPrevious && previousLastState !== 'terminated');

  let summary = 'Fresh browser session or first diagnostics capture';
  if (likelyForceQuitWhileActive) {
    summary =
      'Tab/process restarted after a full close while the page was still active (tab kill / crash / browser quit)';
  } else if (closedFromBackground) {
    summary =
      'Page was hidden/backgrounded, then the tab/browser was fully closed and reopened';
  } else if (hadPrevious && previousLastState === 'terminated') {
    summary = 'Page relaunched after a clean unload signal';
  }

  return {
    restartedAfterFullClose,
    likelyForceQuitWhileActive,
    closedFromBackground,
    gapMs,
    previousSessionId: prevMeta?.sessionId,
    currentSessionId: nextSessionId,
    previousLastState,
    previousLastActiveAt,
    summary,
  };
}

function recordLifecycle(event, detail, persistImmediate = false) {
  pushRing(
    lifecycleBreadcrumbs,
    {
      type: 'lifecycle',
      event,
      at: nowIso(),
      detail: detail || undefined,
    },
    MAX_LIFECYCLE
  );
  if (persistImmediate) {
    if (persistTimer) clearTimeout(persistTimer);
    persistNow();
  } else {
    schedulePersist();
  }
}

function hydrateFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    lastPathname = parsed?.lastPathname || lastPathname;

    navigationBreadcrumbs.splice(
      0,
      navigationBreadcrumbs.length,
      ...trimByAge(parsed?.navigation, MAX_NAV)
    );
    httpBreadcrumbs.splice(0, httpBreadcrumbs.length, ...trimByAge(parsed?.http, MAX_HTTP));
    errorBreadcrumbs.splice(
      0,
      errorBreadcrumbs.length,
      ...trimByAge(parsed?.errors, MAX_ERRORS)
    );
    lifecycleBreadcrumbs.splice(
      0,
      lifecycleBreadcrumbs.length,
      ...trimByAge(parsed?.lifecycle, MAX_LIFECYCLE)
    );
    return parsed?.sessionMeta || null;
  } catch {
    return null;
  }
}

/**
 * Record a route change breadcrumb.
 */
export function recordNavigation(pathname, search = '', hash = '') {
  if (typeof pathname !== 'string' || !pathname) return;
  const full = `${pathname}${search || ''}${hash || ''}`;
  if (full === lastPathname) return;
  const from = lastPathname;
  lastPathname = full;
  pushRing(
    navigationBreadcrumbs,
    {
      type: 'navigation',
      from,
      to: sanitizePath(full),
      at: nowIso(),
    },
    MAX_NAV
  );
  schedulePersist();
}

/**
 * Record an HTTP request/response breadcrumb (success or failure).
 */
export function recordHttpBreadcrumb({ method, url, status, durationMs, errorCode, ok }) {
  const path = sanitizePath(url);
  if (path.includes('/support/tickets')) return;
  pushRing(
    httpBreadcrumbs,
    {
      type: 'http',
      method: method ? String(method).toUpperCase() : undefined,
      url: path,
      status: typeof status === 'number' ? status : undefined,
      durationMs: typeof durationMs === 'number' ? Math.round(durationMs) : undefined,
      errorCode: errorCode ? truncate(errorCode, 120) : undefined,
      ok: Boolean(ok),
      at: nowIso(),
    },
    MAX_HTTP
  );
  schedulePersist();
}

/**
 * Record a client-side error breadcrumb.
 */
export function recordErrorBreadcrumb({ message, source, stack }) {
  pushRing(
    errorBreadcrumbs,
    {
      type: 'error',
      message: truncate(message, MAX_STRING),
      source: truncate(source, 80),
      stack: stack ? truncate(stack, MAX_STACK) : undefined,
      at: nowIso(),
    },
    MAX_ERRORS
  );
  schedulePersist();
}

export function recordLifecycleEvent(event, detail) {
  recordLifecycle(event, detail);
}

function getTheme() {
  try {
    if (document.documentElement.classList.contains('dark')) return 'dark';
    if (document.body?.classList?.contains('dark')) return 'dark';
    const stored = localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') return stored;
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    /* ignore */
  }
  return 'light';
}

function getConnectionInfo() {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return undefined;
    return {
      effectiveType: conn.effectiveType || undefined,
      downlink: typeof conn.downlink === 'number' ? conn.downlink : undefined,
      rtt: typeof conn.rtt === 'number' ? conn.rtt : undefined,
      saveData: Boolean(conn.saveData),
    };
  } catch {
    return undefined;
  }
}

function getSessionHints() {
  let role;
  let userId;
  let email;
  let phone;
  let username;
  let firstName;
  let lastName;
  let name;
  let department;
  let position;
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const user = JSON.parse(raw);
      role = user?.role || undefined;
      userId = user?.id || user?.userId || undefined;
      email = user?.email || undefined;
      phone = user?.phone || undefined;
      username = user?.username || undefined;
      firstName = user?.firstName || undefined;
      lastName = user?.lastName || undefined;
      name =
        user?.name ||
        [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
        undefined;
      department = user?.department || undefined;
      position = user?.position || undefined;
    }
  } catch {
    /* ignore */
  }

  const impersonation = getImpersonationState();
  const isImpersonating = Boolean(impersonation.active);
  return {
    userId: userId || undefined,
    role: role || undefined,
    email: email || undefined,
    phone: phone || undefined,
    username: username || undefined,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    name: name || undefined,
    department: department || undefined,
    position: position || undefined,
    employeeId:
      localStorage.getItem('employeeID') ||
      localStorage.getItem('employeeId') ||
      undefined,
    tenantId: localStorage.getItem('tenantId') || undefined,
    deviceId: localStorage.getItem('deviceId') || undefined,
    language: localStorage.getItem('language') || undefined,
    isImpersonating,
    impersonationSessionId: isImpersonating
      ? impersonation.sessionId || undefined
      : undefined,
  };
}

function getPowerInfo() {
  // Chromium-only; resolve asynchronously is awkward in sync collect — best-effort cache.
  try {
    if (typeof navigator === 'undefined' || typeof navigator.getBattery !== 'function') {
      return undefined;
    }
    // Populate cache in background for later reports
    if (!getPowerInfo._cachePromise) {
      getPowerInfo._cachePromise = navigator
        .getBattery()
        .then((battery) => {
          getPowerInfo._cache = {
            batteryLevel:
              typeof battery.level === 'number'
                ? Math.round(battery.level * 100)
                : undefined,
            charging: typeof battery.charging === 'boolean' ? battery.charging : undefined,
            chargingTime:
              typeof battery.chargingTime === 'number' ? battery.chargingTime : undefined,
            dischargingTime:
              typeof battery.dischargingTime === 'number'
                ? battery.dischargingTime
                : undefined,
          };
          return getPowerInfo._cache;
        })
        .catch(() => undefined);
    }
    return getPowerInfo._cache || undefined;
  } catch {
    return undefined;
  }
}

function getTimeInfo() {
  const deviceNow = Date.now();
  let timezone;
  let locale;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    locale = Intl.DateTimeFormat().resolvedOptions().locale || navigator.language;
  } catch {
    timezone = undefined;
    locale = navigator?.language;
  }
  return {
    deviceNowIso: new Date(deviceNow).toISOString(),
    deviceNowMs: deviceNow,
    timezone,
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    locale,
  };
}

/**
 * Initialize persistence, lifecycle, and global error listeners once.
 */
export function initSupportDiagnostics() {
  if (diagnosticsInitialized || typeof window === 'undefined') return;
  diagnosticsInitialized = true;

  try {
    lastPathname = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;
  } catch {
    lastPathname = null;
  }

  // Warm battery cache early (Chromium) so first ticket often has power info.
  try {
    getPowerInfo();
  } catch {
    /* ignore */
  }

  currentSessionId = newSessionId();
  const prevMeta = hydrateFromStorage();
  continuity = computeContinuity(prevMeta, currentSessionId);

  recordLifecycle(
    'app_cold_start',
    { platform: 'web', continuity, note: continuity.summary },
    true
  );

  if (continuity.restartedAfterFullClose) {
    recordLifecycle(
      continuity.likelyForceQuitWhileActive
        ? 'app_reopened_after_force_close'
        : 'app_reopened_after_full_close',
      {
        gapMs: continuity.gapMs,
        previousSessionId: continuity.previousSessionId,
        previousLastState: continuity.previousLastState,
        previousLastActiveAt: continuity.previousLastActiveAt,
        human: continuity.likelyForceQuitWhileActive
          ? 'User fully closed the browser tab/window while it was active, then opened the app again'
          : 'User fully closed the tab/browser after it was hidden, then opened the app again',
      },
      true
    );
  }

  window.addEventListener('error', (event) => {
    recordErrorBreadcrumb({
      message: event?.message || event?.error?.message || 'window.error',
      source: 'window.onerror',
      stack: event?.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message =
      (typeof reason === 'string' && reason) ||
      reason?.message ||
      'unhandledrejection';
    recordErrorBreadcrumb({
      message,
      source: 'unhandledrejection',
      stack: reason?.stack,
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      pageState = 'hidden';
      recordLifecycle(
        'app_background',
        { platform: 'web', note: 'Tab became hidden' },
        true
      );
    } else if (document.visibilityState === 'visible') {
      pageState = 'active';
      recordLifecycle('app_foreground', {
        platform: 'web',
        note: 'Tab became visible without full reload',
      });
    }
  });

  window.addEventListener('pagehide', () => {
    pageState = 'background';
    recordLifecycle(
      'app_pagehide',
      { platform: 'web', note: 'pagehide — tab closing, navigating away, or bfcache' },
      true
    );
  });

  window.addEventListener('beforeunload', () => {
    pageState = 'background';
    try {
      // Best-effort mark for next boot; browsers may truncate work here.
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed.sessionMeta = {
        ...(parsed.sessionMeta || {}),
        sessionId: currentSessionId,
        lastState: 'terminated',
        lastActiveAt: nowIso(),
      };
      parsed.navigation = navigationBreadcrumbs.slice(-MAX_NAV);
      parsed.http = httpBreadcrumbs.slice(-MAX_HTTP);
      parsed.errors = errorBreadcrumbs.slice(-MAX_ERRORS);
      parsed.lifecycle = [
        ...lifecycleBreadcrumbs.slice(-(MAX_LIFECYCLE - 1)),
        {
          type: 'lifecycle',
          event: 'app_terminated',
          at: nowIso(),
          detail: { platform: 'web', note: 'beforeunload — clean close signal' },
        },
      ];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      /* ignore */
    }
  });
}

/**
 * Build the clientContext payload attached to support ticket create.
 */
export function collectClientContext() {
  if (typeof window === 'undefined') {
    return { collectedAt: nowIso(), platform: 'web' };
  }

  // eslint-disable-next-line no-restricted-globals
  const screenObj = typeof screen !== 'undefined' ? screen : null;
  let timezone;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }

  const session = getSessionHints();
  const connection = getConnectionInfo();
  const time = getTimeInfo();
  const power = getPowerInfo();
  // Warm battery cache for subsequent reports
  getPowerInfo();

  return {
    collectedAt: nowIso(),
    identity: {
      source: 'client',
      userId: session.userId,
      username: session.username,
      email: session.email,
      phone: session.phone,
      firstName: session.firstName,
      lastName: session.lastName,
      fullName: session.name,
      role: session.role,
      language: session.language || 'ar',
      tenantId: session.tenantId,
      employee: session.employeeId
        ? {
            employeeId: session.employeeId,
            department: session.department,
            position: session.position,
          }
        : session.department || session.position
          ? { department: session.department, position: session.position }
          : undefined,
    },
    app: {
      version: getAppVersion(),
      platform: 'web',
      language: session.language || 'ar',
      buildId: process.env.REACT_APP_BUILD_ID || undefined,
    },
    page: {
      url: sanitizePath(window.location.href),
      pathname: truncate(window.location.pathname, 200),
      search: sanitizePath(window.location.search || ''),
      hash: truncate(window.location.hash || '', 120),
      title: truncate(document.title, 200),
    },
    device: {
      deviceId: session.deviceId,
      userAgent: truncate(navigator.userAgent, 400),
      platform: truncate(navigator.platform, 80),
      language: navigator.language,
      languages: Array.isArray(navigator.languages)
        ? navigator.languages.slice(0, 5)
        : undefined,
      timezone: time.timezone || timezone,
      timezoneOffset: time.timezoneOffsetMinutes,
      viewport: {
        w: window.innerWidth,
        h: window.innerHeight,
      },
      screen: screenObj
        ? {
            w: screenObj.width,
            h: screenObj.height,
            dpr: window.devicePixelRatio || 1,
            colorDepth: screenObj.colorDepth,
          }
        : undefined,
      online: typeof navigator.onLine === 'boolean' ? navigator.onLine : undefined,
      connection,
      theme: getTheme(),
      touch: navigator.maxTouchPoints > 0,
      hardwareConcurrency: navigator.hardwareConcurrency || undefined,
      time,
      power: power || undefined,
    },
    session: {
      userId: session.userId,
      role: session.role,
      tenantId: session.tenantId,
      email: session.email,
      phone: session.phone,
      employeeId: session.employeeId,
      isImpersonating: session.isImpersonating,
      impersonationSessionId: session.impersonationSessionId,
      sessionId: currentSessionId || undefined,
      continuity: continuity || undefined,
      diagnosticsPersistedAcrossRestarts: true,
    },
    breadcrumbs: {
      navigation: navigationBreadcrumbs.slice(-MAX_NAV),
      http: httpBreadcrumbs.slice(-MAX_HTTP),
      errors: errorBreadcrumbs.slice(-MAX_ERRORS),
      lifecycle: lifecycleBreadcrumbs.slice(-MAX_LIFECYCLE),
    },
  };
}

export default {
  initSupportDiagnostics,
  collectClientContext,
  recordNavigation,
  recordHttpBreadcrumb,
  recordErrorBreadcrumb,
  recordLifecycleEvent,
};
